// components/CustomerType.js
import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Button, TextField, ListItem, ListItemText } from '@mui/material';
import {
  RowContainer,
  BaseLabel,
  AutoComplete,
  Label,
  SectionTitle,
  InputHalf,
} from '../../../../styles/styleUtils';
import CampoMonetario from '../../components/CampoMonetario';

import { getGenericData, getCatalogoKitsEstoque } from '../../../api';
import { montarCatalogoKitsEstoque, filtrarKitsPorFornecedor } from '../catalogoKitsEstoque';

function KitDetail({ formData, kitSelecionado, onKitChange, onAutoCompleteChange, onInputChange }) {
  const [fornecedor, setFornecedor] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCatalogo, setErroCatalogo] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const kitsFornecedor = useMemo(() => filtrarKitsPorFornecedor(catalogo, formData.idFornecedor),
    [catalogo, formData.idFornecedor]);
  const opcoes = kitSelecionado?.historico ? [kitSelecionado, ...kitsFornecedor] : kitsFornecedor;

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErroCatalogo(false);
    Promise.all([getGenericData('fornecedor', {}), getCatalogoKitsEstoque()])
      .then(([fornecedores, fontes]) => {
        if (!Array.isArray(fornecedores)) throw new Error('Fornecedores indisponíveis');
        const kits = montarCatalogoKitsEstoque(fontes);
        if (ativo) {
          setFornecedor(fornecedores);
          setCatalogo(kits);
        }
      })
      .catch(() => {
        if (ativo) {
          setCatalogo([]);
          setErroCatalogo(true);
        }
      })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [tentativa]);

  return (
    <div>
      <RowContainer>
        <BaseLabel htmlFor="Fornecedor">Fornecedor</BaseLabel>
        <AutoComplete
          disablePortal
          id="fornecedor"
          name="fornecedor"
          disabled={carregando || erroCatalogo}
          value={formData.fornecedor || null}
          onChange={(_, value) => onAutoCompleteChange('idFornecedor', 'fornecedor', value)}
          options={fornecedor}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          renderOption={(props, option) => (
            <ListItem key={option._id} {...props}>
              <ListItemText primary={option.nome} secondary={option.razaoSocial} />
            </ListItem>
          )}
          getOptionLabel={(option) => `${option.nome} (${option._id})`}
          renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="descricao">Descrição</BaseLabel>
        <AutoComplete
          disablePortal
          id="descricao"
          name="descricao"
          freeSolo={false}
          clearOnBlur
          value={kitSelecionado || null}
          options={opcoes}
          disabled={!formData.idFornecedor || carregando || erroCatalogo}
          loading={carregando}
          loadingText="Carregando kits..."
          noOptionsText="Nenhum kit ativo cadastrado para este fornecedor."
          onChange={(_, kit) => onKitChange(kit)}
          isOptionEqualToValue={(option, value) => option.chaveCatalogo === value.chaveCatalogo}
          getOptionLabel={(option) => option.nome || ''}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.chaveCatalogo}>
              <ListItemText primary={option.nome}
                secondary={option.historico ? 'Cadastro de estoque existente'
                  : `${option.categoria}${kitsFornecedor.filter((kit) => kit.nome === option.nome).length > 1
                    ? ` · Ref. ${String(option._id).slice(-6)}` : ''}`} />
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder={formData.idFornecedor ? 'Selecione um kit cadastrado' : 'Selecione o fornecedor primeiro'}
              helperText={kitSelecionado?.historico
                ? 'Descrição existente preservada. Selecione um modelo para padronizar.'
                : 'Selecione na lista para usar o nome exato do kit cadastrado.'} />
          )}
        />
      </RowContainer>
      {erroCatalogo && (
        <Alert severity="error" action={<Button color="inherit" onClick={() => setTentativa((previous) => previous + 1)}>Tentar novamente</Button>}>
          Não foi possível carregar os fornecedores e modelos de kits.
        </Alert>
      )}
      <RowContainer>
        <BaseLabel htmlFor="valor">Valor Compra</BaseLabel>
        <CampoMonetario name={'valorCompra'} valor={formData.valorCompra || 0} onChange={onInputChange} />
      </RowContainer>

      <SectionTitle>Detalhamento dos Itens</SectionTitle>
      <RowContainer>
        <BaseLabel htmlFor="moduloFotovoltaico">Módulo Fotovoltáico</BaseLabel>
        <InputHalf
          type="text"
          id="moduloFotovoltaico"
          name="moduloFotovoltaico"
          value={formData.moduloFotovoltaico ?? ''}
          onChange={onInputChange}
        />
        <div style={{ width: 100 }} />
        <Label htmlFor="quantidadeModulo">Quantidade</Label>
        <InputHalf
          type="number"
          id="quantidadeModulo"
          name="quantidadeModulo"
          value={formData.quantidadeModulo ?? ''}
          onChange={onInputChange}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="inversor">Inversor Fotovoltáico</BaseLabel>
        <InputHalf type="text" id="inversor" name="inversor" value={formData.inversor ?? ''} onChange={onInputChange} />
        <div style={{ width: 100 }} />
        <Label htmlFor="quantidadeInversor">Quantidade</Label>
        <InputHalf
          type="number"
          id="quantidadeInversor"
          name="quantidadeInversor"
          value={formData.quantidadeInversor ?? ''}
          onChange={onInputChange}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="estrutura">Estrutura de Fixação</BaseLabel>
        <InputHalf type="text" id="estrutura" name="estrutura" value={formData.estrutura} onChange={onInputChange} />
        <div style={{ width: 100 }} />
        <Label htmlFor="quantidadeEstrutura">Quantidade</Label>
        <InputHalf
          type="number"
          id="quantidadeEstrutura"
          name="quantidadeEstrutura"
          value={formData.quantidadeEstrutura}
          onChange={onInputChange}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="caboVermelho">Cabo Vermelho</BaseLabel>
        <InputHalf
          type="text"
          id="caboVermelho"
          name="caboVermelho"
          value={formData.caboVermelho}
          onChange={onInputChange}
        />
        <div style={{ width: 100 }} />
        <Label htmlFor="quantidadeCaboVermelho">Quantidade</Label>
        <InputHalf
          type="number"
          id="quantidadeCaboVermelho"
          name="quantidadeCaboVermelho"
          value={formData.quantidadeCaboVermelho}
          onChange={onInputChange}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="caboPreto">Cabo Preto</BaseLabel>
        <InputHalf type="text" id="caboPreto" name="caboPreto" value={formData.caboPreto} onChange={onInputChange} />
        <div style={{ width: 100 }} />
        <Label htmlFor="quantidadeCaboPreto">Quantidade</Label>
        <InputHalf
          type="number"
          id="quantidadeCaboPreto"
          name="quantidadeCaboPreto"
          value={formData.quantidadeCaboPreto}
          onChange={onInputChange}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="disjuntor">Disjuntor</BaseLabel>
        <InputHalf type="text" id="disjuntor" name="disjuntor" value={formData.disjuntor} onChange={onInputChange} />
        <div style={{ width: 100 }} />
        <Label htmlFor="quantidadeDisjuntor">Quantidade</Label>
        <InputHalf
          type="number"
          id="quantidadeDisjuntor"
          name="quantidadeDisjuntor"
          value={formData.quantidadeDisjuntor}
          onChange={onInputChange}
        />
      </RowContainer>
    </div>
  );
}

export default KitDetail;
